import { Flex, Text } from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import { useUserState } from '~/atoms/UserState'
import { DropdownSelect } from '~/components/shared/Dropdown/DropdownSelect'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { MangoInput } from '~/components/shared/MangoInput'
import { MangoModal } from '~/components/shared/MangoModal'
import {
  useCreateCustomStatus,
  useUpdateCustomStatus,
} from '~/queries/customStatusQueries'
import { Availability } from '~/types/AgentCustomStatus'
import { showSuccessToast } from '~/utils/toast'

import { useCustomStatusModalStyle } from './componentStyles/CustomStatusModal.style'

//add onCreate callback prop
export const CustomStatusModal = ({
  onCreated,
}: {
  onCreated?: () => void
}) => {
  const { t } = useTranslation()
  const labelRef = useRef<HTMLInputElement>(null)
  const descriptionRef = useRef<HTMLInputElement>(null)
  const [labelText, setLabelText] = useState<string>('')
  const [descriptionText, setDescriptionText] = useState<string>('')
  const [availability, setAvailability] = useState<Availability>('Away')
  const {
    customStatusModal: [customStatusModalState, setCustomStatusModalState],
  } = useModalState()

  const { pbxId } = useUserState()

  const { mutate: createCustomStatus, isPending: isCreating } =
    useCreateCustomStatus(() => {
      onCreated?.()
      showSuccessToast({ message: t('customStatusModal.toast.createSuccess') })
    })

  const { mutate: updateCustomStatus, isPending: isUpdating } =
    useUpdateCustomStatus(() => {
      showSuccessToast({ message: t('customStatusModal.toast.editSuccess') })
    })

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLabelText(e.target.value)
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescriptionText(e.target.value)
  }

  const handleTypeChange = (value: Availability) => {
    setAvailability(value)
  }

  useEffect(() => {
    setLabelText(customStatusModalState.customStatus?.name || '')
    setDescriptionText(customStatusModalState.customStatus?.description || '')
    setAvailability(customStatusModalState.customStatus?.availability || 'Away')
  }, [customStatusModalState])

  const handleClose = () => {
    setCustomStatusModalState({ open: false })
    setLabelText('')
    setDescriptionText('')
    setAvailability('Away')
  }

  const handleSave = () => {
    if (!pbxId) return
    if (labelText && descriptionText) {
      if (customStatusModalState.customStatus?.id) {
        updateCustomStatus({
          statusId: customStatusModalState.customStatus.id,
          data: {
            pbx_id: pbxId,
            name: labelText,
            description: descriptionText,
            availability: availability,
          },
        })
      } else {
        createCustomStatus({
          pbx_id: pbxId,
          name: labelText,
          description: descriptionText,
          availability: availability,
        })
      }
      handleClose()
    }
    if (!labelText) {
      labelRef.current?.focus()
    }
    if (!descriptionText) {
      descriptionRef.current?.focus()
    }
  }

  const styles = useCustomStatusModalStyle()

  return (
    <MangoModal
      open={customStatusModalState.open}
      onOpenChange={(details) => {
        if (!details.open) handleClose()
      }}
      header={
        customStatusModalState.customStatus?.id
          ? t('customStatusModal.editHeader')
          : t('customStatusModal.NameAddrHeader')
      }
      body={
        <Flex css={styles.bodyContainer}>
          <Flex css={styles.inputContainer}>
            <Text css={styles.inputLabel}>
              {t('customStatusModal.inputLabel')}
            </Text>
            <MangoInput
              ref={labelRef}
              variant="mango"
              placeholder={t('customStatusModal.inputPlaceholder')}
              value={labelText}
              onChange={handleLabelChange}
            />
          </Flex>
          <Flex css={styles.inputContainer}>
            <Text css={styles.inputLabel}>{t('customStatusModal.type')}</Text>
            <DropdownSelect
              placeholder={t('customStatusModal.typePlaceholder')}
              selectedValue={availability}
              items={[
                {
                  value: 'Away',
                  selectedLabel: t('customStatusModal.away'),
                  content: (
                    <MenuListItem
                      leftContent={
                        <Flex
                          css={{
                            width: '1rem',
                            height: '1rem',
                            borderRadius: '6.25rem',
                            background: 'var(--chakra-colors-red-emphasized)',
                            marginRight: '0.25rem',
                          }}
                        />
                      }
                      primaryText={t('customStatusModal.away')}
                    />
                  ),
                },
                {
                  value: 'Online',
                  selectedLabel: t('customStatusModal.online'),
                  content: (
                    <MenuListItem
                      leftContent={
                        <Flex
                          css={{
                            width: '1rem',
                            height: '1rem',
                            borderRadius: '6.25rem',
                            background: 'var(--chakra-colors-brand-emphasized)',
                            marginRight: '0.25rem',
                          }}
                        />
                      }
                      primaryText={t('customStatusModal.online')}
                    />
                  ),
                },
              ]}
              onSelect={handleTypeChange}
            />
          </Flex>
          <Flex css={styles.inputContainer}>
            <Text css={styles.inputLabel}>
              {t('customStatusModal.description')}
            </Text>
            <MangoInput
              ref={descriptionRef}
              variant="mango"
              placeholder={t('customStatusModal.descriptionPlaceholder')}
              value={descriptionText}
              onChange={handleDescriptionChange}
            />
          </Flex>
        </Flex>
      }
      footer={
        <Flex css={styles.footerContainer}>
          <MangoButton
            variant={MangoButtonVariant.Default_100}
            onClick={handleClose}
            disabled={isCreating || isUpdating}
          >
            {t('customStatusModal.cancelButton')}
          </MangoButton>
          <MangoButton
            variant={MangoButtonVariant.Default_300}
            onClick={handleSave}
            disabled={isCreating || isUpdating}
          >
            {t('customStatusModal.saveButton')}
          </MangoButton>
        </Flex>
      }
    />
  )
}
